/**
 * Schemas dos cadastros (contrato, módulo "Cadastros"). Usados pela API para
 * validar entrada e pelo web para validar formulários — uma só definição.
 */
import { z } from 'zod';
import { TIPOS_AUSENCIA } from '../avaliacao/avaliar-periodo.js';
import { FUSO_HORARIO_PADRAO, fusoHorarioValido } from '../tempo/periodo.js';
import { idSchema } from './index.js';
const nome = z.string().trim().min(1, 'Informe o nome').max(80, 'Máximo de 80 caracteres');
const ordem = z.int().nonnegative().default(0);
const ativo = z.boolean().default(true);
/** Código curto para identificar postos/produtos (ex.: FRQ-01, VOL-COM). */
const codigo = z
    .string()
    .trim()
    .min(1, 'Informe o código')
    .max(30, 'Máximo de 30 caracteres')
    .regex(/^[A-Za-z0-9_.-]+$/, 'Use letras, números, ponto, hífen ou _')
    .transform((c) => c.toUpperCase());
export const horaSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use o formato HH:mm');
export const linhaSchema = z.object({ nome, ordem, ativo });
export const setorSchema = z.object({
    nome,
    linhaId: idSchema.nullable().default(null),
    ordem,
    ativo,
});
export const postoSchema = z.object({ setorId: idSchema, codigo, nome, ordem, ativo });
/**
 * Etapa da sequência de um produto. O tempo é informado como o cliente
 * pensa (peças por hora); a conversão para segundos por peça é feita ao salvar.
 */
export const etapaSchema = z.object({
    setorId: idSchema,
    nome,
    pecasPorHora: z.number().positive('Peças por hora deve ser maior que zero'),
    tempoPresetSeg: z.int().nonnegative('Pré-set não pode ser negativo').default(0),
});
export const produtoSchema = z.object({
    codigo,
    nome,
    linhaId: idSchema.nullable().default(null),
    tamanhoLote: z.int().positive('Tamanho da ficha deve ser maior que zero'),
    multiploCaixa: z.int().positive().nullable().default(null),
    ativo,
    etapas: z.array(etapaSchema).min(1, 'Informe ao menos uma etapa da sequência'),
});
export const TIPOS_PAUSA = ['PESSOAL', 'TECNICA', 'FALTA_MATERIAL', 'MANUTENCAO', 'OUTRO'];
export const motivoPausaSchema = z.object({
    codigo: z
        .string()
        .trim()
        .min(1, 'Informe o código')
        .max(30)
        .regex(/^[a-z][a-z0-9_]*$/, 'Use letras minúsculas, números e _'),
    nome,
    tipo: z.enum(TIPOS_PAUSA),
    contaComoProdutivo: z.boolean().default(false),
    /** Pausa para ajudar outro posto: só conta coberta por ajuda confirmada (ADR 0015). */
    ajudaOutroPosto: z.boolean().default(false),
    /** "Fim do expediente": a pausa da noite, fora do tempo parado (ADR 0016). */
    fimDoExpediente: z.boolean().default(false),
    ordem,
    ativo,
});
/** Atividade sem ficha (limpeza, empilhadeira…): o operador escolhe no tablet (K44). */
export const atividadeSchema = z.object({
    nome,
    /** Conta como tempo trabalhado na produtividade; nunca entra na eficiência. */
    contaComoProdutivo: z.boolean().default(true),
    ordem,
    ativo,
});
/** Motivo de perda ao concluir com menos peças (defeito, erro de máquina…). */
export const motivoPerdaSchema = z.object({ nome, ordem, ativo });
export const origemPedidoSchema = z.object({ nome, ativo });
const turnoBase = z.object({
    nome,
    inicio: horaSchema,
    fim: horaSchema,
    fimSexta: horaSchema.nullable().default(null),
    /** Intervalo de refeição: sai do tempo disponível (o almoço não pesa contra ninguém). */
    intervaloInicio: horaSchema.nullable().default(null),
    intervaloFim: horaSchema.nullable().default(null),
    diasSemana: z
        .array(z.int().min(0).max(6))
        .min(1, 'Escolha ao menos um dia')
        .transform((dias) => [...new Set(dias)].sort((a, b) => a - b)),
    ativo,
});
function intervaloCoerente(t, ctx) {
    if ((t.intervaloInicio == null) !== (t.intervaloFim == null)) {
        ctx.addIssue({
            code: 'custom',
            path: [t.intervaloInicio == null ? 'intervaloInicio' : 'intervaloFim'],
            message: 'Informe o início e o fim do intervalo (ou deixe os dois vazios)',
        });
    }
    else if (t.intervaloInicio != null && t.intervaloInicio === t.intervaloFim) {
        ctx.addIssue({
            code: 'custom',
            path: ['intervaloFim'],
            message: 'O fim do intervalo precisa ser depois do início',
        });
    }
}
export const turnoSchema = turnoBase.superRefine(intervaloCoerente);
export const atualizarTurnoSchema = turnoBase
    .extend({ id: idSchema })
    .superRefine(intervaloCoerente);
export const PERFIS = ['DIRETORIA', 'GESTAO', 'OPERADOR'];
const emailOpcional = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('E-mail inválido'))
    .nullable()
    .default(null);
const senhaSchema = z.string().min(8, 'Senha deve ter ao menos 8 caracteres');
const pinSchema = z.string().regex(/^\d{4,6}$/, 'PIN deve ter de 4 a 6 dígitos');
/**
 * Troca da própria senha (gestão e diretoria). A atual confirma que é a
 * pessoa; a nova segue a mesma regra do cadastro e vem confirmada.
 */
export const trocarSenhaSchema = z
    .object({
    senhaAtual: z.string().min(1, 'Informe a senha atual'),
    novaSenha: senhaSchema,
    confirmacao: z.string(),
})
    .superRefine((d, ctx) => {
    if (d.novaSenha !== d.confirmacao) {
        ctx.addIssue({ code: 'custom', path: ['confirmacao'], message: 'As senhas não conferem' });
    }
    else if (d.novaSenha === d.senhaAtual) {
        ctx.addIssue({
            code: 'custom',
            path: ['novaSenha'],
            message: 'A nova senha precisa ser diferente da atual',
        });
    }
});
const usuarioBase = z.object({
    nome,
    perfil: z.enum(PERFIS),
    email: emailOpcional,
    /** Base da curva dos 60 dias. */
    admitidoEm: z.coerce.date().nullable().default(null),
    turnoId: idSchema.nullable().default(null),
    ativo,
    senha: senhaSchema.optional(),
    pin: pinSchema.optional(),
});
function exigirCredenciais(u, ctx, criando) {
    if (u.perfil === 'OPERADOR') {
        if (criando && !u.pin) {
            ctx.addIssue({ code: 'custom', path: ['pin'], message: 'Operador precisa de PIN' });
        }
        return;
    }
    if (!u.email) {
        ctx.addIssue({
            code: 'custom',
            path: ['email'],
            message: 'Gestão e diretoria precisam de e-mail',
        });
    }
    if (criando && !u.senha) {
        ctx.addIssue({ code: 'custom', path: ['senha'], message: 'Informe uma senha inicial' });
    }
}
/** Criação: operador exige PIN; gestão/diretoria exigem e-mail e senha. */
export const criarUsuarioSchema = usuarioBase.superRefine((u, ctx) => exigirCredenciais(u, ctx, true));
/** Edição: senha/PIN só quando informados (troca). */
export const atualizarUsuarioSchema = usuarioBase
    .extend({ id: idSchema })
    .superRefine((u, ctx) => exigirCredenciais(u, ctx, false));
export const CRITERIOS_FILA = ['PRAZO', 'ENTRADA'];
/** Telefone do suporte no WhatsApp: só dígitos com DDI (ex.: 5516999990000). */
export const whatsappSchema = z
    .string()
    .trim()
    .transform((v) => v.replace(/\D/g, ''))
    .pipe(z.string().regex(/^\d{10,15}$/, 'Informe DDI + DDD + número, só dígitos'));
export const configuracaoFabricaSchema = z.object({
    criterioFila: z.enum(CRITERIOS_FILA),
    /** Fuso IANA que define "o dia" da fábrica nas consultas. */
    fusoHorario: z
        .string()
        .trim()
        .default(FUSO_HORARIO_PADRAO)
        .refine(fusoHorarioValido, 'Fuso horário inválido (ex.: America/Sao_Paulo)'),
    /** Canal de suporte dentro do app (RF-T13); vazio desliga o link. */
    whatsappSuporte: z
        .union([z.literal(''), whatsappSchema])
        .nullable()
        .default(null)
        .transform((v) => (v === '' ? null : v)),
    /** Minutos de ida e volta aceitos ao redor de cada ajuda a outro posto (ADR 0015). */
    toleranciaDeslocamentoMin: z.coerce
        .number()
        .int('Use minutos inteiros')
        .min(0, 'Não pode ser negativo')
        .max(60, 'No máximo 60 minutos')
        .default(5),
    /** Minutos que o tablet espera o "vai continuar?" no fim do turno (ADR 0016). */
    esperaFimTurnoMin: z.coerce
        .number()
        .int('Use minutos inteiros')
        .min(1, 'No mínimo 1 minuto')
        .max(120, 'No máximo 120 minutos')
        .default(15),
});
/**
 * Ausência (RF-C08): feriado da fábrica (sem operador) ou atestado/férias/
 * outro de um operador, num intervalo de datas de calendário. Dia com
 * ausência não tem jornada: não é falta nem conta tempo disponível.
 */
export const ausenciaSchema = z
    .object({
    operadorId: idSchema.nullable().default(null),
    tipo: z.enum(TIPOS_AUSENCIA),
    inicio: z.coerce.date(),
    fim: z.coerce.date(),
    descricao: z.string().trim().max(120).nullable().default(null),
})
    .refine((a) => a.fim >= a.inicio, { path: ['fim'], message: 'Fim antes do início' })
    .refine((a) => (a.tipo === 'FERIADO') === (a.operadorId == null), {
    path: ['operadorId'],
    message: 'Feriado vale para todos (sem operador); os outros tipos pedem um operador',
});
/** Ano da importação de feriados nacionais (RF-C08): limite de sanidade, não regra. */
export const importarFeriadosSchema = z.object({
    ano: z
        .int()
        .min(2000, 'Informe um ano entre 2000 e 2100')
        .max(2100, 'Informe um ano entre 2000 e 2100'),
});
export const prioridadeFichaSchema = z.object({
    fichaId: idSchema,
    /** null limpa a prioridade manual. */
    prioridadeManual: z.int().positive().nullable(),
});
//# sourceMappingURL=cadastros.js.map